import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  DocumentReference,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { updateProfile, onAuthStateChanged, getIdToken } from 'firebase/auth';
import { db, auth } from '../../config/firebase';
import { ClinicSettings } from '../../types/clinic';

export interface UserSettings {
  name: string;
  email: string;
  phone: string;
  professionalType: string;
  specialty: string;
  crm: string;
  uf: string;
  bio: string;
  photoURL: string;
}

// Removido: ClinicSettings duplicado - usando o tipo de frontend/src/types/clinic.ts

export interface NotificationSettings {
  email: boolean;
  sms: boolean;
  push: boolean;
  emailNotifications: boolean;
  smsNotifications: boolean;
  appointmentReminders: boolean;
  systemUpdates: boolean;
  marketingEmails: boolean;
}

export interface SecuritySettings {
  twoFactorAuth: boolean;
  sessionTimeout: number;
  passwordExpiry: number;
  loginAlerts: boolean;
}

export interface CompleteUserSettings {
  profile: UserSettings;
  clinic: ClinicSettings;
  notifications: NotificationSettings;
  security: SecuritySettings;
  generalSettings?: any;
  updatedAt: Date;
}

class UserSettingsService {
  // Referência do documento do usuário
  protected getUserDoc(userId: string): DocumentReference {
    return doc(db, 'users', userId, 'settings', 'profile');
  }

  // Cache de configurações
  private settingsCache: Map<string, { data: CompleteUserSettings; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos

  // Carregar configurações do usuário (com cache)
  async loadUserSettings(userId: string, useCache: boolean = true): Promise<CompleteUserSettings | null> {
    try {
      // Verificar cache primeiro
      if (useCache) {
        const cached = this.settingsCache.get(userId);
        if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
          console.log('✅ [CACHE] Configurações carregadas do cache');
          return cached.data;
        }
      }

      console.log('🔄 Carregando configurações do usuário:', userId);
      
      const userDoc = await getDoc(this.getUserDoc(userId));
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        // Log apenas campos relevantes para evitar referências circulares
        console.log('✅ Configurações carregadas:', {
          profile: data?.profile ? {
            name: data.profile.name,
            email: data.profile.email,
            professionalType: data.profile.professionalType,
            specialty: data.profile.specialty
          } : null,
          hasClinic: !!data?.clinic,
          hasNotifications: !!data?.notifications
        });
        
        // Converter Timestamp do Firestore para Date
        const updatedAt = data.updatedAt?.toDate
          ? data.updatedAt.toDate()
          : data.updatedAt instanceof Date
            ? data.updatedAt
            : new Date();
        
        const settings = {
          ...data,
          updatedAt
        } as CompleteUserSettings;

        // Armazenar no cache
        if (useCache) {
          this.settingsCache.set(userId, { data: settings, timestamp: Date.now() });
        }

        return settings;
      } else {
        console.log('ℹ️ Configurações não encontradas para o usuário:', userId);
        return null;
      }
    } catch (error) {
      console.error('❌ Erro ao carregar configurações:', error);
      throw error;
    }
  }

  // Invalidar cache
  invalidateCache(userId: string): void {
    this.settingsCache.delete(userId);
    console.log('🗑️ [CACHE] Cache invalidado para usuário:', userId);
  }

  // Salvar configurações do perfil
  async saveProfileSettings(userId: string, profileSettings: UserSettings): Promise<void> {
    try {
      // Log apenas campos relevantes para evitar referências circulares
      console.log('🔄 [USER-SETTINGS] Salvando configurações do perfil:', {
        name: profileSettings.name,
        email: profileSettings.email,
        phone: profileSettings.phone,
        professionalType: profileSettings.professionalType,
        specialty: profileSettings.specialty,
        crm: profileSettings.crm,
        uf: profileSettings.uf,
        bio: profileSettings.bio ? '...' : '',
        photoURL: profileSettings.photoURL ? '...' : ''
      });
      console.log('👤 [USER-SETTINGS] UserID:', userId);
      console.log('💾 [USER-SETTINGS] professionalType sendo salvo:', profileSettings.professionalType);
      
      // Verificar se o userId é válido
      if (!userId || userId.trim() === '') {
        console.error('❌ [USER-SETTINGS] UserID inválido ou vazio');
        throw new Error('UserID inválido');
      }
      
      // Verificar se o usuário está autenticado no Firebase Auth (opcional, mas preferível)
      const currentFirebaseUser = auth.currentUser;
      if (currentFirebaseUser) {
        // Se há um usuário autenticado, verificar se o userId corresponde
        if (userId !== currentFirebaseUser.uid) {
          console.warn('⚠️ [USER-SETTINGS] UserID não corresponde ao usuário autenticado, mas continuando...');
          console.log('UserID fornecido:', userId);
          console.log('UID do usuário autenticado:', currentFirebaseUser.uid);
          // Não lançar erro aqui, pois pode ser um caso legítimo (ex: admin salvando para outro usuário)
        } else {
          console.log('✅ [USER-SETTINGS] Usuário autenticado e UserID corresponde:', currentFirebaseUser.email);
        }
      } else {
        // Se não há usuário autenticado no Firebase Auth, mas temos um userId válido,
        // podemos continuar (pode ser um caso onde o auth ainda não sincronizou)
        console.warn('⚠️ [USER-SETTINGS] auth.currentUser está null, mas continuando com userId fornecido:', userId);
      }
      
      const userDocRef = this.getUserDoc(userId);
      console.log('📄 Referência do documento:', userDocRef.path);
      
      // Verificar se auth.currentUser está disponível
      const currentAuthUser = auth.currentUser;
      console.log('🔐 [USER-SETTINGS] Verificando autenticação:', {
        userIdFornecido: userId,
        authCurrentUserUid: currentAuthUser?.uid,
        authCurrentUserEmail: currentAuthUser?.email,
        correspondem: currentAuthUser?.uid === userId
      });
      
      // IMPORTANTE: Se auth.currentUser está null, aguardar de forma mais robusta
      // O problema é que o onAuthStateChanged pode não disparar se já foi chamado antes
      // Vamos usar uma abordagem que verifica o estado atual e aguarda se necessário
      if (!currentAuthUser) {
        console.log('⏳ [USER-SETTINGS] auth.currentUser está null, aguardando autenticação...');
        
        // Primeiro, verificar se há um listener ativo que pode nos dar o usuário
        let resolved = false;
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => {
            if (!resolved) {
              resolved = true;
              unsubscribe();
              // Se o timeout ocorrer, continuar mesmo assim - o Firestore pode ter o token em cache
              console.warn('⚠️ [USER-SETTINGS] Timeout aguardando autenticação, continuando...');
              resolve();
            }
          }, 2000); // Reduzir timeout para 2 segundos
          
          // Verificar imediatamente se o usuário já está disponível
          const checkUser = () => {
            const user = auth.currentUser;
            if (user && user.uid === userId) {
              if (!resolved) {
                resolved = true;
                clearTimeout(timeout);
                unsubscribe();
                console.log('✅ [USER-SETTINGS] Autenticação pronta:', user.uid);
                resolve();
              }
            }
          };
          
          // Verificar imediatamente
          checkUser();
          
          // Se ainda não temos o usuário, aguardar o onAuthStateChanged
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user && user.uid === userId) {
              if (!resolved) {
                resolved = true;
                clearTimeout(timeout);
                unsubscribe();
                console.log('✅ [USER-SETTINGS] Autenticação pronta via listener:', user.uid);
                resolve();
              }
            }
          });
          
          // Verificar novamente após um pequeno delay
          setTimeout(checkUser, 100);
          setTimeout(checkUser, 500);
        });
      }
      
      // Verificar novamente após aguardar
      const finalAuthUser = auth.currentUser;
      if (finalAuthUser && finalAuthUser.uid !== userId) {
        // Durante o onboarding, pode haver uma situação onde o userId ainda não corresponde
        // Verificar se estamos em um contexto onde isso é aceitável
        const isOnboarding = window.location.pathname.includes('/onboarding') || 
                            sessionStorage.getItem('onboarding_in_progress') === 'true';
        
        if (isOnboarding) {
          console.warn('⚠️ [USER-SETTINGS] userId não corresponde durante onboarding, mas continuando...', {
            finalAuthUserUid: finalAuthUser.uid,
            userIdFornecido: userId
          });
          // Continuar mesmo assim durante onboarding
        } else {
          console.warn('⚠️ [USER-SETTINGS] userId não corresponde ao usuário autenticado (continuando mesmo assim):', {
            finalAuthUserUid: finalAuthUser.uid,
            userIdFornecido: userId
          });
          // NÃO lançar erro - pode ser problema temporário de sincronização ou caso legítimo
          // (ex: admin salvando para outro usuário, ou durante onboarding)
        }
      }
      
      // Se ainda não temos auth.currentUser, mas temos userId válido,
      // continuar mesmo assim - o Firestore pode ter o token em cache
      if (!finalAuthUser) {
        console.warn('⚠️ [USER-SETTINGS] auth.currentUser ainda está null, mas continuando com userId:', userId);
        console.warn('⚠️ [USER-SETTINGS] O Firestore pode ter o token em cache, tentando salvar mesmo assim...');
      }
      
      // Tentar ler o documento primeiro para verificar permissões
      try {
        console.log('🔍 Verificando permissões de leitura...');
        const existingDoc = await getDoc(userDocRef);
        console.log('✅ Permissões de leitura OK. Documento existe:', existingDoc.exists());
        
        console.log('💾 Tentando salvar dados...');
        
        if (existingDoc.exists()) {
          // Atualizar documento existente
          await updateDoc(userDocRef, {
            profile: profileSettings,
            updatedAt: new Date()
          });
          console.log('✅ [USER-SETTINGS] Documento atualizado com sucesso');
          console.log('💾 [USER-SETTINGS] professionalType salvo no Firebase:', profileSettings.professionalType);
        } else {
          // Criar documento com configurações padrão
          // Tentar obter token atualizado, mas não falhar se não conseguir
          const currentUser = auth.currentUser;
          if (currentUser) {
            // Forçar atualização do token antes de salvar
            try {
              await getIdToken(currentUser, true); // true = força refresh
              console.log('✅ [USER-SETTINGS] Token atualizado antes de salvar');
            } catch (tokenError) {
              console.warn('⚠️ [USER-SETTINGS] Erro ao atualizar token, continuando mesmo assim:', tokenError);
            }
          } else {
            console.warn('⚠️ [USER-SETTINGS] auth.currentUser está null ao criar documento');
            console.warn('⚠️ [USER-SETTINGS] Tentando salvar mesmo assim - Firestore pode ter token em cache');
          }
          
          const defaultSettings: any = {
            profile: profileSettings,
            clinic: {
              nomeFantasia: '',
              razaoSocial: '',
              cnpj: '',
              telefone: '',
              celular: '',
              email: '',
              site: '',
              cep: '',
              endereco: '',
              numero: '',
              complemento: '',
              bairro: '',
              cidade: '',
              estado: '',
              horarioFuncionamento: {
                segunda: { inicio: '08:00', fim: '18:00', ativo: true },
                terca: { inicio: '08:00', fim: '18:00', ativo: true },
                quarta: { inicio: '08:00', fim: '18:00', ativo: true },
                quinta: { inicio: '08:00', fim: '18:00', ativo: true },
                sexta: { inicio: '08:00', fim: '18:00', ativo: true },
                sabado: { inicio: '08:00', fim: '12:00', ativo: false },
                domingo: { inicio: '08:00', fim: '12:00', ativo: false },
              },
              facebook: '',
              instagram: '',
              whatsapp: '',
              clinicId: userId,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            },
            notifications: {
              email: false,
              sms: false,
              push: false,
              emailNotifications: true,
              smsNotifications: false,
              appointmentReminders: true,
              systemUpdates: true,
              marketingEmails: false
            },
            security: {
              twoFactorAuth: false,
              sessionTimeout: 60,
              passwordExpiry: 90,
              loginAlerts: true
            },
            updatedAt: serverTimestamp()
          };
          
          await setDoc(userDocRef, defaultSettings);
          console.log('✅ Documento criado com sucesso');
        }
      } catch (docError: any) {
        const errorCode = docError?.code || '';
        const errorMessage = docError?.message || '';
        
        // Se for erro de permissão, mas estamos em onboarding, tentar continuar
        if (errorCode === 'permission-denied') {
          const isOnboarding = window.location.pathname.includes('/onboarding') || 
                              sessionStorage.getItem('onboarding_in_progress') === 'true';
          
          if (isOnboarding) {
            console.warn('⚠️ [USER-SETTINGS] Erro de permissão durante onboarding, mas continuando...');
            // Não lançar erro durante onboarding
            return;
          }
        }
        
        // Se for erro de "userId não corresponde", não lançar (já tratado acima)
        if (errorMessage.includes('userId não corresponde')) {
          console.warn('⚠️ [USER-SETTINGS] Erro de userId já tratado, continuando...');
          return;
        }
        
        console.error('❌ [USER-SETTINGS] Erro ao acessar documento:', {
          code: errorCode,
          message: errorMessage,
          error: docError
        });
        throw docError;
      }

      // Atualizar o perfil do Firebase Auth se necessário (apenas se houver usuário autenticado)
      if (currentFirebaseUser && currentFirebaseUser.uid === userId) {
        if (currentFirebaseUser.displayName !== profileSettings.name || currentFirebaseUser.photoURL !== profileSettings.photoURL) {
          try {
            console.log('🔄 [USER-SETTINGS] Atualizando perfil do Firebase Auth...');
            await updateProfile(currentFirebaseUser, {
              displayName: profileSettings.name,
              photoURL: profileSettings.photoURL
            });
            console.log('✅ [USER-SETTINGS] Perfil do Firebase Auth atualizado');
          } catch (authError) {
            console.error('⚠️ [USER-SETTINGS] Erro ao atualizar perfil do Auth (não crítico):', authError);
            // Não falhar por causa disso
          }
        }
      } else {
        console.log('ℹ️ [USER-SETTINGS] Pulando atualização do Firebase Auth (usuário não autenticado ou userId diferente)');
      }
      
      // Invalidar cache após salvar para garantir que dados atualizados sejam carregados
      this.invalidateCache(userId);
      console.log('✅ Configurações do perfil salvas com sucesso');
      console.log('🗑️ [CACHE] Cache invalidado após salvar perfil');
    } catch (error: any) {
      console.error('❌ Erro ao salvar configurações do perfil:', error);
      console.error('❌ Stack trace:', error?.stack);
      
      // Se for erro de permissão, salvar no localStorage como fallback
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        console.warn('⚠️ [USER-SETTINGS] ERRO DE PERMISSÃO - Salvando no localStorage como fallback');
        try {
          localStorage.setItem('user_profile_' + userId, JSON.stringify(profileSettings));
          localStorage.setItem('onboarding_completed_' + userId, 'true');
          console.log('✅ [USER-SETTINGS] Dados salvos no localStorage (fallback por permissão)');
          return; // Não lançar erro - consideramos sucesso
        } catch (localError) {
          console.error('❌ Erro ao salvar no localStorage:', localError);
        }
      }
      
      throw error;
    }
  }

  // Função auxiliar para remover campos undefined de um objeto
  private removeUndefinedFields(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedFields(item));
    }
    
    if (typeof obj === 'object' && obj.constructor === Object) {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = this.removeUndefinedFields(value);
        }
      }
      return cleaned;
    }
    
    return obj;
  }

  // Salvar configurações da clínica
  async saveClinicSettings(userId: string, clinicSettings: ClinicSettings): Promise<void> {
    try {
      console.log('🔄 [USER-SETTINGS] Salvando configurações da clínica para userId:', userId);
      
      // Verificar se o userId é válido
      if (!userId || userId.trim() === '') {
        console.error('❌ [USER-SETTINGS] UserID inválido ou vazio');
        throw new Error('UserID inválido');
      }
      
      // VALIDAÇÃO DE SEGURANÇA: Verificar se o usuário está autenticado (opcional, mas preferível)
      const currentFirebaseUser = auth.currentUser;
      if (currentFirebaseUser) {
        // Se há um usuário autenticado, verificar se o userId corresponde
        if (userId !== currentFirebaseUser.uid) {
          console.warn('⚠️ [USER-SETTINGS] UserID não corresponde ao usuário autenticado, mas continuando...');
          console.log('UserID fornecido:', userId);
          console.log('UID do usuário autenticado:', currentFirebaseUser.uid);
        } else {
          console.log('✅ [USER-SETTINGS] Usuário autenticado e UserID corresponde:', currentFirebaseUser.email);
        }
      } else {
        console.warn('⚠️ [USER-SETTINGS] auth.currentUser está null, mas continuando com userId fornecido:', userId);
      }
      
      // VALIDAÇÃO DE ISOLAMENTO: clinicId canônico da clínica (clinic-* ou uid legado)
      // Não forçar clinicId = userId — o portal e o agendamento online usam clinic-{hash}
      const userDocRef = this.getUserDoc(userId);
      let portalClinicId =
        (clinicSettings.clinicId && String(clinicSettings.clinicId).trim()) || userId;
      try {
        const userSnap = await getDoc(userDocRef);
        const fromProfile = userSnap.exists() ? userSnap.data()?.clinicId : null;
        if (fromProfile && String(fromProfile).trim()) {
          portalClinicId = String(fromProfile).trim();
        }
      } catch {
        /* ignore */
      }
      clinicSettings.clinicId = portalClinicId;

      console.log('✅ [USER-SETTINGS] clinicId canônico (portal):', clinicSettings.clinicId);
      
      // CRÍTICO: Remover campos undefined antes de salvar no Firestore
      // Firestore não aceita valores undefined
      const cleanedClinicSettings = this.removeUndefinedFields(clinicSettings);
      
      // Verificar se o documento existe antes de atualizar
      const existingDoc = await getDoc(userDocRef);
      
      if (existingDoc.exists()) {
        // Atualizar documento existente
        await updateDoc(userDocRef, {
          clinic: cleanedClinicSettings,
          updatedAt: new Date()
        });
        console.log('✅ [USER-SETTINGS] Configurações da clínica atualizadas com sucesso');
      } else {
        // Criar documento se não existir
        const defaultSettings: CompleteUserSettings = {
          profile: {
            name: currentFirebaseUser?.displayName || '',
            email: currentFirebaseUser?.email || '',
            phone: '',
            professionalType: '',
            specialty: '',
            crm: '',
            uf: '',
            bio: '',
            photoURL: currentFirebaseUser?.photoURL || ''
          },
          clinic: cleanedClinicSettings,
          notifications: {
            email: false,
            sms: false,
            push: false,
            emailNotifications: true,
            smsNotifications: false,
            appointmentReminders: true,
            systemUpdates: true,
            marketingEmails: false
          },
          security: {
            twoFactorAuth: false,
            sessionTimeout: 60,
            passwordExpiry: 90,
            loginAlerts: true
          },
          updatedAt: new Date()
        };
        
        await setDoc(userDocRef, defaultSettings);
        console.log('✅ [USER-SETTINGS] Documento criado com configurações da clínica');
      }
      
      console.log('✅ [USER-SETTINGS] Configurações da clínica salvas com sucesso para userId:', userId);

      // Sync canônico → clinic_public_info (portal do paciente) no clinicId real
      try {
        const { patientPortalService } = await import('../patientPortal/patientPortalService');
        await patientPortalService.syncClinicToPublic(portalClinicId, cleanedClinicSettings);
        // Legado: espelhar também em uid quando diferente (links antigos)
        if (portalClinicId !== userId) {
          await patientPortalService.syncClinicToPublic(userId, cleanedClinicSettings).catch(() => undefined);
        }
        console.log('✅ [USER-SETTINGS] Clínica sincronizada com portal público:', portalClinicId);
      } catch (syncError) {
        console.warn('⚠️ [USER-SETTINGS] Sync portal falhou (clínica privada ok):', syncError);
      }

      // Espelhar campos básicos nas configs de agendamento online (best-effort)
      try {
        const { bookingGridsService } = await import('../patients/bookingGridsService');
        const existing = await bookingGridsService.getOnlineSettings(portalClinicId).catch(() => null);
        await bookingGridsService.saveOnlineSettings(portalClinicId, {
          ...(existing || { clinicId: portalClinicId, enabled: false }),
          establishmentName:
            cleanedClinicSettings.nomeFantasia ||
            cleanedClinicSettings.nomeClinica ||
            (existing as any)?.establishmentName ||
            '',
          phone: cleanedClinicSettings.telefone || (existing as any)?.phone || '',
          whatsapp: cleanedClinicSettings.whatsapp || cleanedClinicSettings.celular || (existing as any)?.whatsapp || '',
          email: cleanedClinicSettings.email || (existing as any)?.email || '',
          address: [cleanedClinicSettings.endereco, cleanedClinicSettings.numero, cleanedClinicSettings.cidade]
            .filter(Boolean)
            .join(', '),
          publicSlug: (existing as any)?.publicSlug || portalClinicId,
        } as any);
      } catch (bookingSyncError) {
        console.warn('⚠️ [USER-SETTINGS] Sync online-booking falhou:', bookingSyncError);
      }
    } catch (error) {
      console.error('❌ [USER-SETTINGS] Erro ao salvar configurações da clínica:', error);
      throw error;
    }
  }

  // Salvar configurações de notificações
  async saveNotificationSettings(userId: string, notificationSettings: NotificationSettings): Promise<void> {
    try {
      console.log('🔄 Salvando configurações de notificações:', notificationSettings);
      
      // Verificar se o userId é válido
      if (!userId || userId.trim() === '') {
        console.error('❌ [USER-SETTINGS] UserID inválido ou vazio');
        throw new Error('UserID inválido');
      }
      
      // Verificar se o usuário está autenticado no Firebase Auth (opcional, mas preferível)
      const currentFirebaseUser = auth.currentUser;
      if (currentFirebaseUser) {
        if (userId !== currentFirebaseUser.uid) {
          console.warn('⚠️ [USER-SETTINGS] UserID não corresponde ao usuário autenticado, mas continuando...');
        } else {
          console.log('✅ [USER-SETTINGS] Usuário autenticado:', currentFirebaseUser.email);
        }
      } else {
        console.warn('⚠️ [USER-SETTINGS] auth.currentUser está null, mas continuando com userId fornecido:', userId);
      }
      
      await updateDoc(this.getUserDoc(userId), {
        notifications: notificationSettings,
        updatedAt: new Date()
      });
      
      // Invalidar cache após salvar para garantir que dados atualizados sejam carregados
      this.invalidateCache(userId);
      console.log('✅ Configurações de notificações salvas com sucesso');
      console.log('🗑️ [CACHE] Cache invalidado após salvar notificações');
    } catch (error) {
      console.error('❌ Erro ao salvar configurações de notificações:', error);
      throw error;
    }
  }

  // Salvar configurações de segurança
  async saveSecuritySettings(userId: string, securitySettings: SecuritySettings): Promise<void> {
    try {
      console.log('🔄 Salvando configurações de segurança:', securitySettings);
      
      await updateDoc(this.getUserDoc(userId), {
        security: securitySettings,
        updatedAt: new Date()
      });
      
      console.log('✅ Configurações de segurança salvas com sucesso');
    } catch (error) {
      console.error('❌ Erro ao salvar configurações de segurança:', error);
      throw error;
    }
  }

  // Obter especialidade do usuário com mapeamento completo
  async getUserSpecialty(userId: string): Promise<string | null> {
    try {
      console.log('🔄 [UserSettingsService] Detectando especialidade para userId:', userId);
      
      const settings = await this.loadUserSettings(userId);
      
      if (!settings?.profile) {
        console.log('❌ [UserSettingsService] Nenhum profile encontrado');
        return null;
      }
      
      // Log apenas campos relevantes para evitar referências circulares
      console.log('📋 [UserSettingsService] Profile carregado:', {
        name: settings.profile.name,
        email: settings.profile.email,
        professionalType: settings.profile.professionalType,
        specialty: settings.profile.specialty,
        crm: settings.profile.crm
      });
      
      // PRIORIDADE 1: Mapear especialidade específica se definida
      if (settings.profile.specialty) {
        console.log('🎯 [UserSettingsService] Especialidade encontrada:', settings.profile.specialty);
        
        // Mapeamento de especialidades específicas para templates EHR
        const specialtyMapping: Record<string, string> = {
          'Clínica Médica': 'MEDICO',
          'Cardiologia': 'MEDICO',
          'Pediatria': 'MEDICO',
          'Ginecologia': 'MEDICO',
          'Dermatologia': 'MEDICO',
          'Ortopedia': 'MEDICO',
          'Endocrinologia': 'MEDICO',
          'Neurologia': 'MEDICO',
          'Enfermagem': 'ENFERMEIRO',
          'Técnico em Enfermagem': 'TECNICO_ENFERMAGEM',
          'Farmácia Clínica': 'FARMACEUTICO',
          'Farmácia Hospitalar': 'FARMACEUTICO',
          'Fisioterapia': 'FISIOTERAPEUTA',
          'Psicologia Clínica': 'PSICOLOGO',
          'Psicologia Hospitalar': 'PSICOLOGO',
          'Odontologia': 'DENTISTA',
          'Endodontia': 'DENTISTA',
          'Ortodontia': 'DENTISTA',
          'Biomedicina': 'BIOMEDICO',
          'Fonoaudiologia': 'FONOAUDIOLOGO',
          'Terapia Ocupacional': 'TERAPEUTA_OCUPACIONAL',
          'Educação Física': 'EDUCADOR_FISICO',
          'Acupuntura': 'ACUPUNTURISTA',
          'Quiropraxia': 'QUIROPRAXISTA',
          'Osteopatia': 'OSTEOPATA',
          'Podologia': 'PODOLOGO',
          'Massoterapia': 'MASSOTERAPEUTA',
          'Nutrição': 'NUTRICIONISTA',
          'Nutrição Clínica': 'NUTRICIONISTA',
          'Nutrição Esportiva': 'NUTRICIONISTA'
        };
        
        const mappedSpecialty = specialtyMapping[settings.profile.specialty];
        if (mappedSpecialty) {
          console.log('✅ [UserSettingsService] Especialidade mapeada:', settings.profile.specialty, '->', mappedSpecialty);
          return mappedSpecialty;
        } else {
          console.log('⚠️ [UserSettingsService] Especialidade não mapeada:', settings.profile.specialty);
        }
      }
      
      // PRIORIDADE 2: Mapear tipo profissional diretamente
      if (settings.profile.professionalType) {
        console.log('🎯 [UserSettingsService] Tipo profissional encontrado:', settings.profile.professionalType);
        
        // Mapeamento direto de tipos profissionais para templates EHR
        const professionalTypeMapping: Record<string, string> = {
          'medico': 'MEDICO',
          'médico': 'MEDICO',
          'doctor': 'MEDICO',
          'enfermeiro': 'ENFERMEIRO',
          'enfermeira': 'ENFERMEIRO',
          'nurse': 'ENFERMEIRO',
          'tecnico_enfermagem': 'TECNICO_ENFERMAGEM',
          'técnico de enfermagem': 'TECNICO_ENFERMAGEM',
          'farmaceutico': 'FARMACEUTICO',
          'farmacêutico': 'FARMACEUTICO',
          'pharmacist': 'FARMACEUTICO',
          'fisioterapeuta': 'FISIOTERAPEUTA',
          'physiotherapist': 'FISIOTERAPEUTA',
          'psicologo': 'PSICOLOGO',
          'psicólogo': 'PSICOLOGO',
          'psychologist': 'PSICOLOGO',
          'dentista': 'DENTISTA',
          'odontologo': 'DENTISTA',
          'odontólogo': 'DENTISTA',
          'dentist': 'DENTISTA',
          'biomedico': 'BIOMEDICO',
          'biomédico': 'BIOMEDICO',
          'fonoaudiologo': 'FONOAUDIOLOGO',
          'fonoaudiólogo': 'FONOAUDIOLOGO',
          'terapeuta_ocupacional': 'TERAPEUTA_OCUPACIONAL',
          'terapeuta ocupacional': 'TERAPEUTA_OCUPACIONAL',
          'educador_fisico': 'EDUCADOR_FISICO',
          'educador físico': 'EDUCADOR_FISICO',
          'acupunturista': 'ACUPUNTURISTA',
          'quiropraxista': 'QUIROPRAXISTA',
          'osteopata': 'OSTEOPATA',
          'podologo': 'PODOLOGO',
          'podólogo': 'PODOLOGO',
          'massoterapeuta': 'MASSOTERAPEUTA',
          'nutricionista': 'NUTRICIONISTA',
          'nutritionist': 'NUTRICIONISTA'
        };
        
        const normalizedType = settings.profile.professionalType.toLowerCase().trim();
        console.log('🔍 [UserSettingsService] Tipo normalizado:', normalizedType);
        
        const mappedType = professionalTypeMapping[normalizedType];
        
        if (mappedType) {
          console.log('✅ [UserSettingsService] Tipo profissional mapeado:', normalizedType, '->', mappedType);
          return mappedType;
        } else {
          console.log('❌ [UserSettingsService] Tipo profissional não mapeado:', normalizedType);
          console.log('🔍 [UserSettingsService] Tipos disponíveis:', Object.keys(professionalTypeMapping));
        }
      }
      
      console.log('❌ [UserSettingsService] Nenhuma especialidade ou tipo profissional encontrado');
      return null;
      
    } catch (error) {
      console.error('❌ [UserSettingsService] Erro ao obter especialidade do usuário:', error);
      return null;
    }
  }

  // Obter tipo profissional do usuário
  async getUserProfessionalType(userId: string): Promise<string | null> {
    try {
      const settings = await this.loadUserSettings(userId);
      return settings?.profile?.professionalType || null;
    } catch (error) {
      console.error('❌ Erro ao obter tipo profissional do usuário:', error);
      return null;
    }
  }

  // Salvar configurações gerais
  async saveGeneralSettings(userId: string, generalSettings: any): Promise<void> {
    try {
      console.log('🔄 Salvando configurações gerais:', generalSettings);
      
      // Verificar se o userId é válido
      if (!userId || userId.trim() === '') {
        console.error('❌ [USER-SETTINGS] UserID inválido ou vazio');
        throw new Error('UserID inválido');
      }
      
      // Verificar se o usuário está autenticado no Firebase Auth (opcional, mas preferível)
      const currentFirebaseUser = auth.currentUser;
      if (currentFirebaseUser) {
        if (userId !== currentFirebaseUser.uid) {
          console.warn('⚠️ [USER-SETTINGS] UserID não corresponde ao usuário autenticado, mas continuando...');
        } else {
          console.log('✅ [USER-SETTINGS] Usuário autenticado:', currentFirebaseUser.email);
        }
      } else {
        console.warn('⚠️ [USER-SETTINGS] auth.currentUser está null, mas continuando com userId fornecido:', userId);
      }
      
      await updateDoc(this.getUserDoc(userId), {
        generalSettings: generalSettings,
        updatedAt: new Date()
      });
      
      console.log('✅ Configurações gerais salvas com sucesso');
    } catch (error) {
      console.error('❌ Erro ao salvar configurações gerais:', error);
      throw error;
    }
  }

  // Marcar onboarding como completo
  async markOnboardingComplete(userId: string): Promise<void> {
    try {
      const onboardingRef = doc(db, 'users', userId, 'settings', 'onboarding');
      await setDoc(onboardingRef, {
        isComplete: true,
        completedAt: Timestamp.now(),
        completedSteps: [0, 1, 2, 3, 4]
      });
      
      console.log('✅ [USER-SETTINGS] Onboarding marcado como completo para usuário:', userId);
      
      // Também salvar no localStorage
      localStorage.setItem('onboarding_completed_' + userId, 'true');
      localStorage.setItem('onboarding_completed', 'true');
      
    } catch (error: any) {
      console.error('❌ [USER-SETTINGS] Erro ao marcar onboarding como completo:', error);
      
      // Se for erro de permissão, usar localStorage e não falhar
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        console.warn('⚠️ [USER-SETTINGS] Erro de permissão - marcando onboarding como completo apenas no localStorage');
        localStorage.setItem('onboarding_completed_' + userId, 'true');
        localStorage.setItem('onboarding_completed', 'true');
        console.log('✅ [USER-SETTINGS] Onboarding marcado como completo no localStorage (fallback)');
        return; // Não lançar erro - consideramos sucesso
      }
      
      throw error;
    }
  }

  // Verificar se o onboarding foi completado
  async isOnboardingComplete(userId: string): Promise<boolean> {
    try {
      console.log('🔍 [USER-SETTINGS] Verificando status do onboarding para:', userId);
      
      // 1. Verificar documento específico de onboarding (verificação principal)
      const onboardingRef = doc(db, 'users', userId, 'settings', 'onboarding');
      const onboardingSnap = await getDoc(onboardingRef);
      
      if (onboardingSnap.exists()) {
        const data = onboardingSnap.data();
        const isComplete = data?.isComplete || false;
        console.log('✅ [USER-SETTINGS] Documento de onboarding encontrado:', isComplete);
        
        // Se está marcado como completo, verificar se tem os dados obrigatórios
        // Mas usar a estrutura correta do documento (settings/profile)
        if (isComplete) {
          // Usar getUserDoc que retorna a referência correta: users/{userId}/settings/profile
          const profileRef = this.getUserDoc(userId);
          const profileSnap = await getDoc(profileRef);
          
          if (profileSnap.exists()) {
            const profileData = profileSnap.data();
            // Os dados estão em profileData.profile, não diretamente em profileData
            const profile = profileData?.profile || {};
            const hasProfessionalType = profile?.professionalType && profile.professionalType.trim() !== '';
            const hasPhone = profile?.phone && profile.phone.trim() !== '';
            const hasName = profile?.name && profile.name.trim() !== '';
            
            console.log('🔍 [USER-SETTINGS] Verificando dados do perfil:', {
              hasProfessionalType,
              hasPhone,
              hasName,
              professionalType: profile?.professionalType,
              phone: profile?.phone,
              name: profile?.name
            });
            
            // Se não tem todos os campos obrigatórios, considerar incompleto
            if (!hasProfessionalType || !hasPhone || !hasName) {
              console.log('⚠️ [USER-SETTINGS] Onboarding marcado como completo mas faltam dados obrigatórios');
              return false;
            }
            
            console.log('✅ [USER-SETTINGS] Onboarding completo e dados verificados');
          } else {
            console.log('⚠️ [USER-SETTINGS] Documento de perfil não encontrado');
            return false;
          }
        }
        
        return isComplete;
      }
      
      // 2. Verificar se usuário tem TODOS os dados obrigatórios do onboarding
      // NÃO apenas displayName/email do Google, mas os campos específicos do onboarding
      console.log('🔄 [USER-SETTINGS] Verificando dados obrigatórios do onboarding...');
      // Usar getUserDoc que retorna a referência correta: users/{userId}/settings/profile
      const profileRef = this.getUserDoc(userId);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        const profileData = profileSnap.data();
        // Os dados estão em profileData.profile, não diretamente em profileData
        const profile = profileData?.profile || {};
        
        // Verificar campos OBRIGATÓRIOS do onboarding:
        // - professionalType (tipo de profissional)
        // - phone (telefone)
        // - name (nome no perfil, não apenas displayName do Google)
        const hasProfessionalType = profile?.professionalType && profile.professionalType.trim() !== '';
        const hasPhone = profile?.phone && profile.phone.trim() !== '';
        const hasName = profile?.name && profile.name.trim() !== '';
        
        console.log('🔍 [USER-SETTINGS] Verificando dados do perfil:', {
          hasProfessionalType,
          hasPhone,
          hasName,
          professionalType: profile?.professionalType,
          phone: profile?.phone,
          name: profile?.name
        });
        
        if (hasProfessionalType && hasPhone && hasName) {
          console.log('✅ [USER-SETTINGS] Usuário tem todos os dados obrigatórios do onboarding');
          // Marcar onboarding como completo apenas se tiver TODOS os campos
          await this.markOnboardingComplete(userId);
          return true;
        } else {
          console.log('⚠️ [USER-SETTINGS] Usuário não tem todos os dados obrigatórios:', {
            hasProfessionalType,
            hasPhone,
            hasName,
            professionalType: profile?.professionalType,
            phone: profile?.phone,
            name: profile?.name
          });
        }
      }
      
      // 3. Fallback: Verificar se usuário tem dados de clínica (usuários muito antigos)
      // Apenas para compatibilidade com usuários existentes antes do onboarding
      const clinicRef = doc(db, 'users', userId, 'settings', 'clinic');
      const clinicSnap = await getDoc(clinicRef);
      
      if (clinicSnap.exists()) {
        const clinicData = clinicSnap.data();
        const hasClinicData = clinicData?.name;
        
        if (hasClinicData) {
          console.log('✅ [USER-SETTINGS] Usuário existente detectado com dados de clínica (compatibilidade)');
          // Verificar também se tem dados de perfil antes de marcar como completo
          const profileRef = this.getUserDoc(userId);
          const profileSnap = await getDoc(profileRef);
          
          if (profileSnap.exists()) {
            const profileData = profileSnap.data();
            // Os dados estão em profileData.profile, não diretamente em profileData
            const profile = profileData?.profile || {};
            const hasProfessionalType = profile?.professionalType && profile.professionalType.trim() !== '';
            const hasPhone = profile?.phone && profile.phone.trim() !== '';
            const hasName = profile?.name && profile.name.trim() !== '';
            
            // Se tem clínica E tem dados de perfil completos, marcar como completo
            if (hasProfessionalType && hasPhone && hasName) {
              await this.markOnboardingComplete(userId);
              return true;
            }
          }
        }
      }
      
      console.log('❌ [USER-SETTINGS] Usuário novo detectado - onboarding necessário');
      return false;
      
    } catch (error) {
      console.error('❌ [USER-SETTINGS] Erro ao verificar status do onboarding:', error);
      return false;
    }
  }
}

export const userSettingsService = new UserSettingsService();
export default userSettingsService; 